import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '@/core/analytics'
import { graphqlClient } from '@/core/api/graphql-client'
import { useAuthStore } from '@/features/auth/store'
import { UserDocument } from '@/generated/graphql'
import { appConfig } from '@/shared/config/app-config'
import { STATIC_LINK, useAppLink } from '@/shared/hooks/use-app-link'
import { useBannerEligible } from '@/shared/hooks/use-day-completed'

const SCREEN = { screenName: 'appBanner', screenType: 'banner' }
const DISMISS_PREFIX = `${appConfig.storagePrefix}_app_banner_dismissed_`

function isMobile() {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
}

function isIOS() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
}

export function SmartAppBanner() {
  const { t } = useTranslation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { data: userData } = useQuery({
    queryKey: ['user'],
    queryFn: () => graphqlClient.request(UserDocument),
    enabled: isAuthenticated,
  })
  const userId = userData?.me.id
  const dismissKey = userId ? `${DISMISS_PREFIX}${userId}` : null
  const [dismissedByAction, setDismissedByAction] = useState(false)
  const { link } = useAppLink()

  const dismissed =
    dismissedByAction ||
    (dismissKey ? localStorage.getItem(dismissKey) === 'true' : false)

  const bannerEligible = useBannerEligible(userId)

  const isStoic = userData?.me.isStoic
  const visible =
    !dismissed &&
    isMobile() &&
    !(isAuthenticated && !userId) &&
    !isStoic &&
    bannerEligible

  const tracked = useRef(false)
  const bannerRef = useCallback((node: HTMLDivElement | null) => {
    if (!node || tracked.current) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          tracked.current = true
          trackEvent('appBannerShown', SCREEN)
          observer.disconnect()
        }
      },
      { threshold: 0.5 },
    )
    observer.observe(node)
  }, [])

  if (!visible) return null

  function handleDismiss() {
    trackEvent('appBannerDismissed', SCREEN)
    if (dismissKey) localStorage.setItem(dismissKey, 'true')
    setDismissedByAction(true)
  }

  function handleOpen() {
    trackEvent('appBannerOpenClicked', {
      ...SCREEN,
      hasAutologin: link !== STATIC_LINK,
    })
    handleDismiss()
  }

  return (
    <div
      ref={bannerRef}
      className="flex shrink-0 items-center border-b border-app-grey-100 bg-white px-[16px] py-[12px] pr-[20px]"
    >
      <div className="flex min-w-0 flex-1 items-center gap-[12px]">
        <img
          src={appConfig.assets.appIcon}
          alt=""
          className="h-[40px] w-[40px] shrink-0 rounded-[10px]"
        />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm text-app-grey-900">
            {appConfig.name}
          </span>
          <span className="truncate text-xs text-app-grey-500">
            Habits that fit your life
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-[14px]">
        <a
          href={link}
          onClick={handleOpen}
          className="flex items-center rounded-full bg-app-primary-600 px-[20px] py-[5px] text-sm font-bold text-white"
        >
          {t(
            isIOS()
              ? 'smart_banner_open_ios_web'
              : 'smart_banner_open_android_web',
          )}
        </a>
        <button
          type="button"
          onClick={handleDismiss}
          className="p-[4px] text-app-grey-400"
        >
          <X className="size-5.5" />
        </button>
      </div>
    </div>
  )
}
